import { c as _c } from "react/compiler-runtime";
import { type AuthError, type AuthUser, auth, confirmLoginMFA as confirmLoginMFAApi, confirmMFAEnrollment as confirmMFAEnrollmentApi, login as loginApi, logout, MultifactorRequiredError, RecaptchaDupeError, RequiresRecentLoginError, triggerLoginMFA, triggerMFAEnrollment as triggerMFAEnrollmentApi } from '@netpurpose/api';
import { useRedirectWithOriginalUrl } from '@netpurpose/core';
import { logger } from '@netpurpose/utils';
import { type MultiFactorResolver, onAuthStateChanged, type RecaptchaVerifier } from 'firebase/auth';
import { useRouter } from 'next/router';
import React, { type FC, useEffect, useState } from 'react';
import { config } from '#services/config';
import { isUnprotectedRoute } from '#services/routes';
type AuthState = {
  isAuthInitialised: boolean;
  isAuthenticated: boolean;
  authError?: AuthError | undefined;
  user?: AuthUser | undefined;
  mfaResolver?: MultiFactorResolver | undefined;
  mfaVerificationId?: string | undefined;
};
export type IAuthContext = AuthState & {
  login: (username: string, password: string, recaptchaVerifier?: RecaptchaVerifier) => Promise<void>;
  logout: () => void;
  triggerMFAEnrollment: (phoneNumber: string, recaptchaVerifier?: RecaptchaVerifier) => Promise<string | null>;
  confirmMFAEnrollment: (code: string, verificationId: string) => Promise<void>;
  confirmLoginMFA: (code: string, verificationId: string, resolver: MultiFactorResolver) => Promise<void>;
  clearMFAState: () => void;
};
const defaultState: AuthState = {
  isAuthInitialised: false,
  isAuthenticated: false
};
export const defaultContext: IAuthContext = {
  ...defaultState,
  logout: () => {},
  login: async () => {},
  triggerMFAEnrollment: async () => null,
  confirmMFAEnrollment: async () => {},
  confirmLoginMFA: async () => {},
  clearMFAState: () => null
};
export const AuthContext = React.createContext(defaultContext);
export const AuthProvider: FC<{
  children: React.ReactNode;
}> = props => {
  const $ = _c(26);
  const [state, setState] = useState(defaultState);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setState(_temp);
      if (!isUnprotectedRoute(location.pathname)) {
        location.assign(`${location.origin}/login`);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onNotLoggedIn = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const unsubscribe = onAuthStateChanged(auth, user => {
        if (!user) {
          onNotLoggedIn();
          return;
        }
        setState(prevState_0 => ({
          ...prevState_0,
          isAuthInitialised: true,
          isAuthenticated: true,
          user
        }));
      });
      return unsubscribe;
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      landingPageUrl: config.routes.landingPage
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    handleRedirect
  } = useRedirectWithOriginalUrl(t3);
  let t4;
  if ($[4] !== handleRedirect || $[5] !== router || $[6] !== state) {
    t4 = async (username, password, recaptchaVerifier) => {
      setState({
        ...state,
        authError: undefined
      });
      ;
      try {
        await loginApi(username, password);
        handleRedirect();
      } catch (t5) {
        const loginError = t5;
        if (loginError instanceof MultifactorRequiredError) {
          ;
          try {
            const {
              resolver
            } = loginError;
            const verificationId = await triggerLoginMFA(resolver, recaptchaVerifier);
            setState({
              ...state,
              mfaVerificationId: verificationId,
              mfaResolver: resolver,
              authError: undefined
            });
            return;
          } catch (t6) {
            const mfaError = t6;
            if (mfaError instanceof Error) {
              setState({
                ...state,
                authError: mfaError
              });
              router.push(config.routes.login);
              return;
            }
          }
        }
        if (loginError instanceof Error) {
          setState({
            ...state,
            authError: loginError
          });
        }
      }
    };
    $[4] = handleRedirect;
    $[5] = router;
    $[6] = state;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const login = t4;
  let t5;
  if ($[8] !== state) {
    t5 = async (phoneNumber, recaptchaVerifier_0) => {
      setState({
        ...state,
        authError: undefined
      });
      ;
      try {
        const verificationId_0 = await triggerMFAEnrollmentApi(phoneNumber, recaptchaVerifier_0);
        return verificationId_0;
      } catch (t6) {
        const err = t6;
        if (err instanceof RecaptchaDupeError) {
          logger.warn("Recaptcha dupe error during MFA enrollment");
          return null;
        }
        if (err instanceof Error) {
          setState({
            ...state,
            authError: err
          });
          if (err instanceof RequiresRecentLoginError) {
            logger.warn("MFA enrollment requires recent login");
            return null;
          }
          logger.error(err);
        }
        return null;
      }
    };
    $[8] = state;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const triggerMFAEnrollment = t5;
  let t6;
  if ($[10] !== router || $[11] !== state) {
    t6 = async (code, verificationId_1) => {
      setState({
        ...state,
        authError: undefined
      });
      ;
      try {
        await confirmMFAEnrollmentApi(code, verificationId_1);
        router.push(config.routes.landingPage);
      } catch (t7) {
        const err_0 = t7;
        if (err_0 instanceof Error) {
          setState({
            ...state,
            authError: err_0
          });
          logger.error(err_0);
        }
      }
    };
    $[10] = router;
    $[11] = state;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const confirmMFAEnrollment = t6;
  let t7;
  if ($[13] !== router || $[14] !== state) {
    t7 = async (code_0, verificationId_2, resolver_0) => {
      setState({
        ...state,
        authError: undefined
      });
      ;
      try {
        await confirmLoginMFAApi(code_0, verificationId_2, resolver_0);
        router.push(config.routes.landingPage);
      } catch (t8) {
        const err_1 = t8;
        if (err_1 instanceof Error) {
          setState({
            ...state,
            authError: err_1
          });
          logger.error(err_1);
        }
      }
    };
    $[13] = router;
    $[14] = state;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const confirmLoginMFA = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setState(_temp2);
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const clearMFAState = t8;
  let t9;
  if ($[17] !== confirmLoginMFA || $[18] !== confirmMFAEnrollment || $[19] !== login || $[20] !== state || $[21] !== triggerMFAEnrollment) {
    t9 = {
      ...state,
      login,
      logout,
      triggerMFAEnrollment,
      confirmMFAEnrollment,
      confirmLoginMFA,
      clearMFAState
    };
    $[17] = confirmLoginMFA;
    $[18] = confirmMFAEnrollment;
    $[19] = login;
    $[20] = state;
    $[21] = triggerMFAEnrollment;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== props || $[24] !== t9) {
    t10 = <AuthContext.Provider value={t9} {...props} />;
    $[23] = props;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === defaultContext) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};
function _temp(prevState) {
  return {
    ...prevState,
    isAuthInitialised: true,
    isAuthenticated: false,
    user: undefined
  };
}
function _temp2(prevState_1) {
  return {
    ...prevState_1,
    mfaVerificationId: undefined,
    mfaResolver: undefined,
    authError: undefined
  };
}